import { isPlainObject } from "lodash";

import { parseYaml } from "utils/valueTransformers";

/**
 * SchemaForm inference: plain-JSON-Schema -> form-element dialect.
 *
 * The {@link SchemaForm} renderer switches on each node's `element` key
 * (`form-object`, `form-string`, `form-integer`, ...). Handed a *plain*
 * JSON Schema (`type`/`properties`, no `element` keys) it used to render a bare
 * "Unknown form element" callout with zero input fields.
 *
 * A flow's `input_schema` is also what the MCP surface exposes as a tool
 * `inputSchema` (plain JSON Schema), so authors naturally write JSON Schema and
 * NOT the form-element dialect. This module makes those schemas render as real
 * forms by inferring an `element` for every node that lacks one, mapping JSON
 * Schema `type` (+ `format`, nullability) to the corresponding dialect element.
 *
 * Conservative by construction:
 * - A schema whose ROOT already carries `element` is the form-element dialect and
 *   is returned completely UNTOUCHED — connector configs, custom-object attribute
 *   schemas and wrapper schemas render byte-identically to before.
 * - An empty (`{}`) or non-object schema is returned unchanged (so the SchemaForm
 *   "no schema" / code-view fallback keeps working).
 * - Only genuinely element-less trees are transformed, and only `element`/`label`
 *   are ADDED — every original key (`type`, `default`, `description`, `required`,
 *   `enum`, ...) is preserved, so the value semantics submitted by the form are
 *   unchanged.
 *
 * A scalar `enum` (a closed set of allowed values) is enriched into a value-based
 * dropdown (see `constElementFor`), value-preserving: the submitted data is the
 * chosen enum member, not an index.
 *
 * A JSON-Schema `number` (floating-point) maps to `form-number`, a real numeric
 * input that accepts decimals (its integer sibling is `form-integer`).
 *
 * Types with no faithful dialect element (`null`, `oneOf`/`anyOf`/`allOf`/`$ref`,
 * or an opaque object with no shape) fall back to `form-json` — a per-field
 * YAML/code editor — instead of "Unknown form element". That keeps the field
 * type-correct (YAML parses the value back to its type) while still being
 * editable.
 */

const NULL_TYPE = "null";

/** Map a JSON-Schema string `format` to its dialect element suffix (base name). */
const STRING_FORMAT_BASE: Record<string, string> = {
    "date-time": "datetime",
    date: "date",
    time: "time",
};

/**
 * Determine the primary (non-null) JSON-Schema type of a node and whether the
 * node is nullable. Handles both `type: "string"` and `type: ["string", "null"]`
 * (and the OpenAPI-style `nullable: true`).
 */
const analyzeType = (node: any): { baseType: string | undefined; nullable: boolean } => {
    let type = node.type;
    let nullable = node.nullable === true;
    if (Array.isArray(type)) {
        nullable = nullable || type.includes(NULL_TYPE);
        const nonNull = type.filter((entry: unknown) => entry !== NULL_TYPE);
        type = nonNull[0];
    }
    return { baseType: typeof type === "string" ? type : undefined, nullable };
};

/** `form-<base>` or, when nullable, `form-optional-<base>`. */
const elementFor = (base: string, nullable: boolean) =>
    nullable ? `form-optional-${base}` : `form-${base}`;

/**
 * The `form-const-<base>` element carrying a single fixed enum member value.
 * The base type only affects seeding of `null` (`form-const-null` → `null`); for
 * every other concrete value the const element resolves to its `const` (see
 * getDefaultRequiredValue), so the exact non-null base is cosmetic. All
 * `form-const-*` elements render as an empty field — the dropdown option label is
 * the visible control, the branch itself shows nothing.
 */
const constElementFor = (value: unknown): string => {
    if (value === null) return "form-const-null";
    switch (typeof value) {
        case "number":
            return "form-const-integer";
        case "boolean":
            return "form-const-boolean";
        default:
            return "form-const-string";
    }
};

/**
 * Recursively infer the form-element dialect for a single JSON-Schema node.
 * `key` is the property name the node sits under in its parent object; it is used
 * as the field label (a node's own `title` wins over the key).
 */
const inferNode = (node: any, key?: string): any => {
    // Non-object schema fragments (e.g. a boolean JSON Schema) can't be enriched.
    if (!isPlainObject(node)) {
        return node;
    }
    // Respect an explicit dialect element if one is already present (defensive:
    // handles a partially-authored / mixed schema without clobbering it).
    if (typeof node.element === "string") {
        return node;
    }

    const label = node.title ?? key;
    const withMeta = (extra: Record<string, unknown>) => ({
        ...node,
        ...(label !== undefined ? { label } : {}),
        ...extra,
    });

    const { baseType, nullable } = analyzeType(node);

    // A constrained `enum` (a fixed, closed set of allowed values) renders as a
    // dropdown. We map it onto the existing (oneOf/anyOf-based) form-dropdown,
    // giving each enum member its own `const` branch. This is VALUE-preserving:
    // the dropdown submits the selected branch's data — a `form-const-*` branch
    // resolves to its `const` (see getDefaultRequiredValue) — NOT the option
    // index, so the flow input still receives the enum member itself. Only scalar
    // enums are enriched; an object/array-typed node keeps its structural
    // rendering even if it also carries an `enum`.
    const enumValues = Array.isArray(node.enum) ? node.enum : undefined;
    const isStructural =
        baseType === "object" ||
        baseType === "array" ||
        isPlainObject(node.properties) ||
        node.items !== undefined ||
        node.additionalProperties !== undefined;
    if (enumValues && enumValues.length > 0 && !isStructural) {
        const anyOf = enumValues.map((value: unknown) => ({
            const: value,
            label: String(value),
            element: constElementFor(value),
        }));
        const matchedIndex =
            node.default !== undefined
                ? enumValues.findIndex((value: unknown) => value === node.default)
                : -1;
        // Emit the dialect's CANONICAL dropdown shape: the `Form element: dropdown`
        // branch of formSchema.json requires `element` + `anyOf` (the choices) +
        // `defaultIndex`. Emitting `oneOf` or omitting `defaultIndex` matches no
        // builder branch, so the EditableSchemaForm element-type picker resolves to
        // index -1 and renders "Invalid choice" (author-time regression). `anyOf`
        // renders identically to `oneOf` — SchemaFormDropdown reads `oneOf ?? anyOf`
        // — and `defaultIndex` defaults to 0 (the first choice) as a builder-authored
        // dropdown does, so a plain enum round-trips cleanly through the builder.
        return withMeta({
            element: elementFor("dropdown", nullable),
            anyOf,
            defaultIndex: matchedIndex >= 0 ? matchedIndex : 0,
        });
    }

    const isObjectNode =
        baseType === "object" ||
        isPlainObject(node.properties) ||
        isPlainObject(node.patternProperties) ||
        node.additionalProperties !== undefined;

    if (isObjectNode) {
        const hasProperties =
            isPlainObject(node.properties) && Object.keys(node.properties).length > 0;
        const hasDynamicProperties =
            isPlainObject(node.patternProperties) ||
            node.additionalProperties === true ||
            isPlainObject(node.additionalProperties);
        if (!hasProperties && !hasDynamicProperties) {
            // An object with no declared shape has nothing to render as a form.
            return withMeta({ element: "form-json" });
        }
        const properties = hasProperties
            ? Object.fromEntries(
                  Object.entries(node.properties).map(([childKey, childNode]) => [
                      childKey,
                      inferNode(childNode, childKey),
                  ])
              )
            : node.properties;
        const additionalProperties = isPlainObject(node.additionalProperties)
            ? inferNode(node.additionalProperties)
            : node.additionalProperties;
        return withMeta({
            element: elementFor("object", nullable),
            ...(properties !== undefined ? { properties } : {}),
            ...(node.additionalProperties !== undefined ? { additionalProperties } : {}),
        });
    }

    const isArrayNode = baseType === "array" || node.items !== undefined;
    if (isArrayNode) {
        const items = isPlainObject(node.items) ? inferNode(node.items) : node.items;
        return withMeta({
            element: elementFor("array", nullable),
            ...(node.items !== undefined ? { items } : {}),
        });
    }

    if (baseType === "string") {
        const formatBase =
            typeof node.format === "string" ? STRING_FORMAT_BASE[node.format] : undefined;
        return withMeta({ element: elementFor(formatBase ?? "string", nullable) });
    }
    if (baseType === "integer") {
        return withMeta({ element: elementFor("integer", nullable) });
    }
    if (baseType === "number") {
        return withMeta({ element: elementFor("number", nullable) });
    }
    if (baseType === "boolean") {
        return withMeta({ element: elementFor("boolean", nullable) });
    }

    // null, enum-without-type, oneOf/anyOf/allOf/$ref, or otherwise
    // unmapped -> graceful per-field code editor (never "Unknown form element").
    return withMeta({ element: "form-json" });
};

/**
 * Normalize a schema for the SchemaForm renderer: enrich a plain JSON Schema into
 * the form-element dialect, or return it unchanged when it is already the dialect
 * (root has `element`), empty, or not an object. See the module doc-comment.
 */
export const normalizeSchemaForForm = (schema: any): any => {
    if (!isPlainObject(schema) || Object.keys(schema).length === 0) {
        return schema;
    }
    if (typeof schema.element === "string") {
        return schema;
    }
    return inferNode(schema);
};

/**
 * Whether a JSON-Schema/dialect root can match a JSON *object* instance.
 *
 * Mirrors the backend `common.utils.schema_root_permits_object` (kept in lock-step
 * so the FE never wraps a root the backend would accept, and always wraps one it
 * would reject). Fails OPEN: returns `true` for any schema that does NOT
 * *explicitly* constrain its root type away from `object` (a missing/empty or
 * type-less schema accepts anything, incl. an object). Only a root that explicitly
 * declares a non-object scalar type — directly, or via every branch of a
 * `oneOf`/`anyOf`, or any branch of an `allOf` — returns `false`.
 * operator-task-fe-input-schema-builder-no-scalar-root
 */
export const schemaRootPermitsObject = (schema: any): boolean => {
    if (!isPlainObject(schema) || Object.keys(schema).length === 0) {
        return true;
    }
    const declared = (schema as any).type;
    if (declared !== undefined && declared !== null) {
        if (Array.isArray(declared)) {
            return declared.includes("object");
        }
        return declared === "object";
    }
    for (const combinator of ["oneOf", "anyOf"] as const) {
        const branches = (schema as any)[combinator];
        if (Array.isArray(branches) && branches.length > 0) {
            // oneOf/anyOf: an object is permitted if AT LEAST ONE branch permits it.
            return branches.some((b) => schemaRootPermitsObject(b));
        }
    }
    const allOf = (schema as any).allOf;
    if (Array.isArray(allOf) && allOf.length > 0) {
        // allOf: an object must satisfy EVERY branch, so all must permit it.
        return allOf.every((b) => schemaRootPermitsObject(b));
    }
    // No explicit type or type-constraining combinator: type-less = permissive.
    return true;
};

/** Derive a snake_case property key from a field's `label` (fallback "input"). */
const propertyKeyFromField = (field: any): string => {
    const label = typeof field?.label === "string" ? field.label : "";
    const key = label
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
    return key || "input";
};

/**
 * Flow inputs are ALWAYS a JSON object, so a flow `input_schema` whose ROOT is a
 * scalar can never run: no dict of inputs satisfies a `string`/`null` top-level
 * schema, so execution-creation always fails and the schema-level `default` is
 * never applied.
 *
 * When the schema builder (form view / enrich / code view) would emit such a
 * scalar-root `input_schema`, wrap it into an object root with the scalar demoted
 * to a single named property (key derived from its `label`), so the authored
 * schema becomes runnable. A root that already permits an object (object-typed,
 * type-less, or an object-permitting combinator) is returned UNCHANGED, as is any
 * non-object value (a not-yet-parsed string, null, …). This mirrors — and
 * pre-empts — the backend write-gate that hard-rejects a scalar-root input_schema.
 * operator-task-fe-input-schema-builder-no-scalar-root
 */
export const wrapScalarRootInputSchema = (schema: any): any => {
    if (schemaRootPermitsObject(schema)) {
        return schema;
    }
    const key = propertyKeyFromField(schema);
    return {
        type: "object",
        element: "form-object",
        properties: { [key]: schema },
    };
};

/**
 * Submit-boundary variant of {@link wrapScalarRootInputSchema}.
 *
 * The `remappingSubmitDto` for the flow `input_schema` field is handed the field
 * value as it currently is: the code-view edit path passes the raw YAML/JSON
 * *string* (the parsed object is only produced on the editor's blur, which a
 * "save before blur" can race), while the form/enrich views pass a parsed
 * object. `wrapScalarRootInputSchema` only transforms objects, so on a string it
 * silently no-ops and a scalar root reaches the backend (which then hard-rejects
 * it). This helper normalizes a string to an object first, wraps a scalar root,
 * and returns the value to PERSIST:
 * - scalar root (string or object) -> the wrapped object root;
 * - a root that already permits an object -> the ORIGINAL value unchanged
 *   (preserves the existing string/object wire form for the common case);
 * - an unparseable string / null / undefined -> returned unchanged.
 * operator-task-fe-input-schema-builder-no-scalar-root
 */
export const wrapScalarRootInputSchemaValue = (value: any): any => {
    let parsed = value;
    if (typeof value === "string") {
        try {
            parsed = parseYaml(value);
        } catch {
            return value;
        }
    }
    if (parsed === undefined || parsed === null) {
        return value;
    }
    const wrapped = wrapScalarRootInputSchema(parsed);
    // Only override when a wrap actually occurred (a scalar root was demoted to
    // an object); otherwise keep the original value (and its string/object form).
    return wrapped !== parsed ? wrapped : value;
};
